import { StatusDetailsRow } from '@/bundle/_Opco/ui/OrganizationDetails/Details/ui/OrganizationUserAccountDetails/styles';
import { Button } from '@/components/Button/Button';
import { ReelAnimateTime } from '@/bundle/shared/components/ReelAnimateTime/ReelAnimateTime';
import { formatDateTimeUTC } from '@/helpers/dateHelpers/dateHelpers';
import { DetailsRow } from '@/components/DetailsWidget/DetailsRow/DetailsRow';
import { useTimer } from '@/hooks/useTimer';
import { useMutation } from '@tanstack/react-query';
import { ToggleBlockUserConfirmModal } from '@/bundle/shared/components/modals/ToggleBlockUserConfirmModal';
import { useParams } from 'react-router';
import {
  blockInternalOrganizationUser,
  reinviteInternalOrganizationUser,
  unblockInternalOrganizationUser,
} from '@/bundle/_MasterAdmin/_OrganizationUserDetailsPage/api/api';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { InternalOrganizationUserType } from '../../types/types';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { AdminUserStatus } from './AdminUserStatus';
import { useState } from 'react';
import { Box } from '@/components/Box/Box';
import { UserDetailsRowTitle } from '@/bundle/_Opco/shared/styles';
import {
  isActiveOrganizationUser,
  isBlockedOrganizationUser,
  isInvitedOrganizationUser,
  isUnconfirmedOrganizationUser,
} from '@/helpers/organizationHelpers';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { UserLimitReachedModal } from '@/bundle/shared/components/UserLimitReachedModal/UserLimitReachedModal';
import { hasResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { ORGANIZATION_USER_LIMIT_REACH_ERROR_MESSAGE } from '@/api/const';

type AdminAccountDetailsStatusActionsType = {
  user: InternalOrganizationUserType;
  isOwnAccount: boolean;
  onRefetchUser: () => void;
};

export const AdminAccountDetailsStatusActions = ({
  user,
  isOwnAccount,
  onRefetchUser,
}: AdminAccountDetailsStatusActionsType) => {
  const { id } = useParams<{ id: string }>();

  const [isToggleBlockUserModalOpen, setIsToggleBlockUserModalOpen] = useState(false);
  const [isResendInvitationOpen, setIsResendInvitationOpen] = useState(false);
  const [openUsersLimitModal, setOpenUsersLimitModal] = useState(false);

  const { mutate: blockInternalOrganizationUserMutate, isPending: isPendingBlockInternalOrganizationUser } =
    useMutation({
      mutationKey: ['block_internal_organization_user'],
      mutationFn: () => blockInternalOrganizationUser(id),
      onSuccess(response) {
        if (response?.error) {
          return showSnackbarResponseError(response?.error);
        }

        onRefetchUser();
        showSnackbar('User has been blocked.');
      },
      onSettled() {
        closeToggleBlockUserModal();
      },
    });

  const { mutate: unblockInternalOrganizationUserMutate, isPending: isPendingUnblockInternalOrganizationUser } =
    useMutation({
      mutationKey: ['unblock_internal_organization_user'],
      mutationFn: () => unblockInternalOrganizationUser(id),
      onSuccess(response) {
        if (response.error) {
          const hasError = hasResponseError(response?.error, ORGANIZATION_USER_LIMIT_REACH_ERROR_MESSAGE);

          if (hasError) {
            return setOpenUsersLimitModal(true);
          }

          return showSnackbarResponseError(response?.error);
        }

        onRefetchUser();
        showSnackbar('User has been unblocked.');
      },
      onSettled() {
        closeToggleBlockUserModal();
      },
    });

  const { mutate: reinviteInternalOrganizationUserMutate, isPending: isPendingReinviteOrganizationUser } = useMutation({
    mutationKey: ['reinvite_internal_organization_user'],
    mutationFn: () => reinviteInternalOrganizationUser(id),
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }
    },
    onSettled() {
      onRefetchUser();
      closeResendInvitationModal();
    },
  });

  const closeResendInvitationModal = () => setIsResendInvitationOpen(false);
  const openToggleBlockUserModal = () => setIsToggleBlockUserModalOpen(true);
  const closeToggleBlockUserModal = () => setIsToggleBlockUserModalOpen(false);
  const openResendInvitationModal = () => setIsResendInvitationOpen(true);

  const { status, invitation_data } = user;
  const isInvited = isInvitedOrganizationUser(status);
  const formattedLastInvitedDate = formatDateTimeUTC(invitation_data?.last_time_invited);
  const isUnconfirmed = isUnconfirmedOrganizationUser(status);
  const isBlocked = isBlockedOrganizationUser(status);
  const isActive = isActiveOrganizationUser(status);
  const { seconds } = useTimer(invitation_data?.next_resend_available_in_sec, onRefetchUser);
  const timesInvited = invitation_data?.times_invited || '-';
  const isAvailableToModify = isActive || isInvited || isUnconfirmed;

  return (
    <>
      <StatusDetailsRow>
        <Box display='flex' justifyContent='space-between'>
          <Box>
            <UserDetailsRowTitle>Status</UserDetailsRowTitle>
            <AdminUserStatus status={status} />
          </Box>

          <Box display='flex' columnGap='12px' alignItems='center'>
            {(isInvited || isUnconfirmed) && (
              <Button
                width={160}
                size='medium'
                variant='secondary'
                onClick={openResendInvitationModal}
                disabled={!!seconds}
              >
                {!seconds ? (
                  'Resend Invitation'
                ) : (
                  <>
                    Resend in &nbsp;
                    <ReelAnimateTime seconds={seconds} />
                  </>
                )}
              </Button>
            )}

            {isAvailableToModify && !isOwnAccount && (
              <Button width={71} size='medium' variant='secondary' onClick={openToggleBlockUserModal}>
                Block
              </Button>
            )}

            {isBlocked && !isOwnAccount && (
              <Button width={90} size='medium' variant='secondary' onClick={openToggleBlockUserModal}>
                Unblock
              </Button>
            )}
          </Box>
        </Box>

        {(isInvited || isUnconfirmed) && (
          <Box marginTop='20px'>
            <DetailsRow name='Last Invited'>{formattedLastInvitedDate}</DetailsRow>
            <DetailsRow name='Times Invited'>{timesInvited}</DetailsRow>
          </Box>
        )}
      </StatusDetailsRow>

      <ToggleBlockUserConfirmModal
        isOpen={isToggleBlockUserModalOpen}
        isLoading={isBlocked ? isPendingUnblockInternalOrganizationUser : isPendingBlockInternalOrganizationUser}
        isBlockedUser={isBlocked}
        onClose={closeToggleBlockUserModal}
        onConfirm={isBlocked ? unblockInternalOrganizationUserMutate : blockInternalOrganizationUserMutate}
      />

      <ConfirmModal
        isOpen={isResendInvitationOpen}
        header='Resend Invitation'
        body={
          <>
            A new invitation will be sent to the invited user. <br /> The previous one will be still valid.
          </>
        }
        onClose={closeResendInvitationModal}
        onConfirm={reinviteInternalOrganizationUserMutate}
        isLoading={isPendingReinviteOrganizationUser}
      />

      <UserLimitReachedModal isOpen={openUsersLimitModal} onClose={() => setOpenUsersLimitModal(false)} />
    </>
  );
};
